// ============================================================
// frontend/src/App.tsx
// ============================================================
// Root component. Responsibilities:
//   1. Wrap everything in AuthProvider
//   2. Show LoginView when not authenticated
//   3. Render the appropriate view based on `view` state
//   4. Provide a top-level error/success toast handler
//   5. Load settings once after auth so they're available to all views
//
// Routing approach: a simple `view` state object instead of a routing
// library. For 7 views, this is simpler than React Router and gives
// us total control over animations and back-navigation. The state
// shape is union of named variants — TypeScript enforces correct
// usage everywhere.
// ============================================================

import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './lib/auth';
import { ConsiliumLoader } from './components/ConsiliumLoader';
import { api } from './lib/api';
import { LoginView } from './views/LoginView';
import { ChangePasswordView } from './views/ChangePasswordView';
import { AdminDashboard } from './views/AdminDashboard';
import { HomeView } from './views/HomeView';
import { ClientsView } from './views/ClientsView';
import { ClientDetailView } from './views/ClientDetailView';
import { SessionDetailView } from './views/SessionDetailView';
import { TemplatesView } from './views/TemplatesView';
import { AllSessionsView } from './views/AllSessionsView';
import { SettingsView } from './views/SettingsView';
import { GuideView } from './views/GuideView';
import { Header } from './components/Header';
import { Toast } from './components/Toast';
import type { Settings, SettingsResponse } from '@consilium/shared';

// ============================================================
// ViewState — the discriminated union for our simple "router"
// ============================================================
// Each variant has a `name` discriminator + any params it needs.
// TypeScript ensures we always pass the right params for each view.
export type ViewState =
  | { name: 'home' }
  | { name: 'clients' }
  | { name: 'client'; clientId: string }
  | { name: 'sessions' }
  | { name: 'session'; sessionId: string }
  | { name: 'templates' }
  | { name: 'settings' }
  | { name: 'guide' };

export function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

// ============================================================
// AppInner — runs inside AuthProvider so it can use useAuth()
// ============================================================
function AppInner() {
  const { therapist, loading } = useAuth();

  // While checking the initial session, show nothing (a flash of login
  // screen followed by the dashboard is jarring).
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)',
      }}>
        <ConsiliumLoader size={80} />
      </div>
    );
  }

  if (!therapist) {
    return <LoginView />;
  }

  // Admin-created accounts land with a temp password and must change it
  // before doing anything else. Gate ahead of the role split so neither
  // super admins nor therapists can bypass it.
  if (therapist.mustChangePassword) {
    return <ChangePasswordView />;
  }

  // Super admins get a completely different UI — no clients/sessions,
  // just the therapist-management dashboard.
  if (therapist.role === 'super_admin') {
    return <AdminDashboard />;
  }

  return <Authenticated />;
}

// ============================================================
// Authenticated — main app shell once logged in
// ============================================================
function Authenticated() {
  const [view, setView] = useState<ViewState>({ name: 'home' });
  const [settings, setSettings] = useState<Settings | null>(null);

  // Toast state (singleton — we only show one at a time)
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' } | null>(null);
  const showError = (message: string) => setToast({ message, type: 'error' });
  const showSuccess = (message: string) => setToast({ message, type: 'success' });

  // Load settings once after auth. Used by views that need defaults
  // (currency for new packages, duration for sessions, etc.).
  useEffect(() => {
    (async () => {
      try {
        const { settings } = await api.get<SettingsResponse>('/settings');
        setSettings(settings);
      } catch (err) {
        // Non-fatal — views have their own fallbacks.
        console.warn('Failed to load settings:', err);
      }
    })();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Header view={view} setView={setView} />
      <main className="container" style={{ paddingTop: 24, paddingBottom: 40 }}>
        {/* Key by view so React unmounts the old view (clean state, exit animation). */}
        <div key={`${view.name}-${('clientId' in view && view.clientId) || ('sessionId' in view && view.sessionId) || ''}`}>
          {view.name === 'home' && (
            <HomeView setView={setView} showError={showError} />
          )}
          {view.name === 'clients' && (
            <ClientsView setView={setView} showError={showError} />
          )}
          {view.name === 'client' && (
            <ClientDetailView
              clientId={view.clientId}
              setView={setView}
              showError={showError}
              settings={settings}
            />
          )}
          {view.name === 'sessions' && (
            <AllSessionsView setView={setView} showError={showError} />
          )}
          {view.name === 'session' && (
            <SessionDetailView
              sessionId={view.sessionId}
              setView={setView}
              showError={showError}
            />
          )}
          {view.name === 'templates' && (
            <TemplatesView showError={showError} settings={settings} />
          )}
          {view.name === 'settings' && (
            <SettingsView
              settings={settings}
              onSettingsUpdated={setSettings}
              showError={showError}
              showSuccess={showSuccess}
            />
          )}
          {view.name === 'guide' && (
            <GuideView setView={setView} />
          )}
        </div>
      </main>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}
